import { hex_to_decimal } from 'src/helpers/utilities';
import type {
	SocketEventsRequest,
	RequestData,
	ResponseData,
	SocketEventsResponse,
} from 'src/types/requests/request_data_socket';
import { get_sockets_length, get_socket } from './socket';
import { getDeviceHost, real_id } from 'src/helpers/interface_helpers';

/**
 * Here we keep track of the request by its `trid`
 * id: the device' id; resolve: the function to resolve the promise
 */
let request: {
	[key: number]: {
		id: string;
		resolve?: (value?: any) => void;
	};
} = {};

/**
 * Monotonic transaction id generator.
 *
 * `Date.now()` alone is not safe as a trid because two requests fired in the
 * same millisecond end up with the same id. That causes the second call to
 * overwrite the first in the in-flight request map, the first response to
 * resolve the wrong promise, and the orphaned promise to time out with a
 * generic "Error when sending socket request:" error. Seeding from `Date.now()`
 * keeps backward compatibility with the cloud expectation of a
 * timestamp-like id while guaranteeing uniqueness across concurrent calls.
 */
let _lastTrid = 0;
const nextTrid = (): number => {
	const now = Date.now();
	_lastTrid = now > _lastTrid ? now : _lastTrid + 1;
	return _lastTrid;
};

/**
 * Send a message via the cloud socket for the particular device/user
 */
export const socket_send = async (
	id: string,
	params: Record<string, any>,
	event: SocketEventsRequest,
	method?: string,
	viaq?: boolean,
	timeout: number = 6000,
) => {
	const realId = real_id(id);
	let data: RequestData;
	let idWithChannel = undefined;
	try {
		if (!(get_sockets_length() > 0)) {
			throw new Error('Socket.no_sockets');
		}

		if (id.includes('_')) {
			// if the id already has a channel, we can use it
			idWithChannel = id;
		} else {
			// if the id does not have a channel, we can use the channel from params
			// id on these methods is a component id, not a device channel
			const skipChannelSuffix =
				method?.startsWith('BTHomeControl.') ||
				method?.startsWith('IR.') ||
				method?.startsWith('IRDevice.') ||
				method?.startsWith('IRCode.');
			idWithChannel = params?.config?.channel
				? id + '_' + params?.config?.channel
				: params?.id != null && !skipChannelSuffix
					? id + '_' + params?.id
					: id;
		}
		// the idea is that sometimes we have a shared channel but not the first one, so in the devices store we have only the realId+ the channel id and to prevent undefined errors we need to add the channel id to the id just to determine the socket. Use `!= null` so component id 0 (e.g. Fan.Set) still appends the channel suffix.

		console.warn('[SOCKET CMD] Sending > ', idWithChannel, method, params);

		const socket = get_socket(getDeviceHost(idWithChannel));

		if (!socket) {
			throw new Error('No socket found');
		}

		// We have different data structures for the two events
		// ref. https://gitlab.allterco.net/Shelly/be/iot-backend/-/blob/master/WebSocketCommunication.md
		switch (event) {
			case 'Shelly:JrpcRequest':
				data = {
					event: event,
					trid: nextTrid(),
					deviceId: realId?.startsWith('X') ? realId : hex_to_decimal(realId),
					method: method,
					params: params,
					...(viaq && { viaq: viaq }),
				};
				break;
			case 'Shelly:XPRequest':
				data = {
					event: event,
					trid: nextTrid(),
					deviceId: realId?.startsWith('X') ? realId : hex_to_decimal(realId),
					method: method,
					params: params,
				};
				break;
			case 'Shelly:CommandRequest':
				data = {
					event: event,
					trid: nextTrid(),
					deviceId: hex_to_decimal(realId),
					data: {
						cmd: 'direct_call',
						params: params,
					},
				};
		}

		socket.send(data);
		request[data.trid] = { id: data.deviceId };

		const res = await new Promise(async (resolve, reject) => {
			request[data.trid].resolve = resolve;

			setTimeout(() => {
				reject({ error: 'Timeout', isok: false });
			}, timeout);
		});

		console.log('[SOCKET CMD] Received < ', idWithChannel, method, res);

		return res;
	} catch (error) {
		// The message is the only part callers see: `gen2CloudReq` reduces this to
		// `error.message`, so the reason has to be in the text rather than the cause.
		const reason = error?.error ?? error?.message ?? error;
		throw new Error(`Error when sending socket request: ${reason}`, { cause: error });
	} finally {
		if (data?.trid) {
			delete request?.[data.trid];
		}
	}
};

/**
 * Handler for response of direct-to-device socket commands
 */
export const handle_socket_event_response = (message: any): void => {
	if (request[message?.trid] !== undefined) {
		let resolveData: ResponseData;

		switch (message.event as SocketEventsResponse) {
			case 'Shelly:JrpcResponse':
			case 'Shelly:XPResponse':
				resolveData = message?.response;
				break;
			case 'Shelly:CommandResponse':
				resolveData = message?.data;
				break;
		}

		request[message.trid]?.resolve(resolveData);
	}
};
